import { DURATION_ALIAS } from '@constants';
import { PRICING_SHOW_RENDERS } from '@constants/params';
import { textInHooks, ParserContentData } from '@libs';
import { IconDiscount, IconRuble, IconCheck, IconInfinity } from '@icons';

// получение короткого описания тарифа для мобилки
const getShortContent = (data) => {
  const items = ParserContentData.getItemsArrayFromBlock(data, { blockKey: 'short_banners' });

  return items.reduce((acc, item) => {
    const { alias, formData } = ParserContentData.getDataFromItem(item);

    acc[alias] = {
      content: formData.raw_description,
    };
    return acc;
  }, {});
};

// Получение контента
const getContent = (contentData) => {
  const short = getShortContent(contentData);

  return { short };
};

// Получение кол-ва доступных пакетов
const getAvailablePackages = (prices) => {
  const values = Object.keys(prices).reduce((acc, item) => {
    if (prices[item]) {
      acc.push(Number(item));
    }

    return acc;
  }, []);

  return { values, text: values.join(', ') };
};

// Получение опшинов для селекта таблицы продуктов
const getOptionsProducts = (countSets) => {
  return countSets
    .sort((a, b) => a - b)
    .map((item) => {
      const option = {};
      const caption =
        item === 1
          ? `{{site.pricing.priceFor}} {{site.pricing.item}}`
          : `{{site.pricing.priceFor}} ${item} {{site.pricing.items}}`;

      option.value = item;
      option.view_value = caption;
      option.render = caption;

      return option;
    });
};

// Получения данных для таблицы тарифов
const getTariffs = ({ showTariffs, user, currency }) => {
  return showTariffs.map((item) => {
    const tariff = {};
    const showInnerTariffs = item.goods
      .filter(
        (good) =>
          (good.alias === 'tariff' || good.alias === 'businessvr') && JSON.parse(good.params).tariff_id === item.id
      )
      .sort((a, b) => DURATION_ALIAS[a.duration] - DURATION_ALIAS[b.duration]);
    const innerTariffs = showInnerTariffs.map((item, index) => {
      const innerTariff = {};

      innerTariff.id = item.id;
      innerTariff.caption = item.caption;
      innerTariff.price = item.price;
      innerTariff.duration = DURATION_ALIAS[item.duration];

      if (index !== 0) {
        const period_price = Math.round(innerTariff.price / innerTariff.duration);
        const discount = 100 - (period_price / showInnerTariffs[0].price) * 100;

        innerTariff.discount = Math.round(discount);
      } else {
        innerTariff.discount = 0;
      }

      return innerTariff;
    });

    const price = innerTariffs.reduce((acc, item) => {
      return {
        ...acc,
        [item.duration]: {
          value: item.isBV ? item.price / item.duration : Math.round(item.price / item.duration),
          total: item.price,
          discount: item.discount,
        },
      };
    }, {});

    const options = innerTariffs.map((item) => {
      const option = {};
      const monthText = item.duration === 1 ? '{{site.pricing.month}}' : '{{site.pricing.months}}';

      option.id = item.id;
      option.view_value = `${item.duration} ${monthText} = ${item.price} ${currency}`; // то что идет в нативный селект
      option.caption = item.caption;
      option.price = `${item.price} ${currency}`;
      option.discount = item.discount;
      option.render_selected = option.view_value;
      option.duration = item.duration;
      option.value = item.duration;

      return option;
    });

    tariff.id = item.id;
    tariff.caption = item.caption;
    tariff.alias = item.alias;
    tariff.price = price;
    tariff.options = innerTariffs.length ? options : [];
    tariff.isCurrent = user ? user.tariff.alias === item.alias : false;
    tariff.isStart = item.alias === 'start';
    tariff.isFree = item.alias === 'start';
    tariff.isStar = (!user || user.tariff.alias === 'start') && item.alias === 'proplus';

    return tariff;
  });
};

// Получения данных для таблицы продуктов
const getProducts = (tariffs, keys) => {
  const products = [];
  const countAvailableSets = [1];
  const availablePackagesWithPrice = {}; // хранит все возможнные пакеты с ценами
  const allPackagesWithProducts = {}; // все пакеты со списком id входящих в пакет товаров

  tariffs.forEach(({ goods }) => {
    goods.forEach((good) => {
      good.in_sets.forEach((set) => {
        if (!(set.id in allPackagesWithProducts)) {
          allPackagesWithProducts[set.id] = [good.id];
        } else if (!allPackagesWithProducts[set.id].includes(good.id)) {
          allPackagesWithProducts[set.id].push(good.id);
        }
      });
    });
  });
  tariffs.forEach((tariff) => {
    const data = tariff.goods.filter((good) => keys.includes(good.alias));

    data.forEach((item) => {
      if (!item.in_sets.length) {
        return;
      }

      const product = {};
      const productInfo = textInHooks(item.caption);

      product.id = item.id;
      product.caption = productInfo.out_hooks;
      product.display = productInfo.display;
      product.resolution = productInfo.in_hooks;
      product.alias = item.alias;

      const isAdded = products.find((render) => render.id === item.id);

      if (!isAdded) {
        products.push(product);
      }
    });
  });

  tariffs.forEach(({ goods, alias }) => {
    if (alias === 'start') {
      return;
    }

    products.forEach((product) => {
      if (!product.display) {
        return;
      }

      const currentGood = goods.find((good) => good.id === product.id) || {};
      const { in_sets, price } = currentGood;
      const prices = { 1: price || 0 };

      if (in_sets) {
        in_sets.forEach(({ id, count, price }) => {
          // проверка, берем ли цену из этого сета
          if (allPackagesWithProducts[id].length > 1) {
            return;
          }

          // установка возможных сетов для селекта
          if (!countAvailableSets.includes(count)) {
            countAvailableSets.push(count);
          }

          prices[count] = price;
        });
      }

      product.tariffs = product.tariffs ? { ...product.tariffs, [alias]: prices } : { [alias]: prices };
    });
  });

  const productsNormal = products
    .filter((item) => item.display)
    .sort((a, b) => (parseInt(a.resolution) || 99999) - (parseInt(b.resolution) || 99999));
  const options = getOptionsProducts(countAvailableSets.slice(1));
  const availablePackages =
    availablePackagesWithPrice.prices !== undefined && getAvailablePackages(availablePackagesWithPrice.prices);

  return { products: productsNormal, options, availablePackages };
};

const getTokens = (pricing) => {
  try {
    let tokens = [];

    for (let i = 0; i < pricing.length; i++) {
      const tokenGood = pricing[i].goods.find((i) => i.alias === 'token');
      if (tokenGood !== undefined) {
        tokenGood.in_sets
          .filter((i) => i.grouping === 'token')
          .map((i) => {
            if (tokens.findIndex((j) => j.id === i.id && j.store_id === i.store_id) === -1) {
              tokens.push(i);
            }
          });
      }
    }

    return tokens;
  } catch (error) {
    console.error(error);
  }
};

const getPriceRenderInTokens = (pricing) => {
  try {
    let tariffWithTokensPrice = null;

    for (let i = 0; i < pricing.length; i++) {
      const tokenGoods = pricing[i]?.goods?.find((item) => item.price_in_tokens);

      if (tokenGoods) {
        tariffWithTokensPrice = pricing[i]?.goods.filter((item) => item.not_for_sale !== true) || {};
        break;
      }
    }

    return tariffWithTokensPrice
      .filter((item) => item.price_in_tokens !== null && item.variables.alias)
      .sort((a, b) => b.alias.localeCompare(a.alias));
  } catch (error) {
    console.error(error);
  }
};

export const normalizePricing = ({ pricing, user, currency, contentData }) => {
  const showTariffs = pricing;
  const renders = getProducts(showTariffs, PRICING_SHOW_RENDERS);
  const tariffs = getTariffs({ showTariffs, user, currency });
  const tokens = getTokens(pricing);
  const goodsWithPriceToken = getPriceRenderInTokens(pricing);
  const tariffsContent = contentData;

  return { renders, tariffs, tariffsContent, tokens, goodsWithPriceToken };
};

export const normalizeTariffText = (data) => {
  try {
    return data.split('\n').map((item) => {
      const firstSymbol = item.slice(0, 1);
      let icon = '';
      let text = item;

      if (firstSymbol === '-') {
        icon = IconCheck;
        text = item.slice(2);
      }

      if (firstSymbol === '*') {
        icon = IconInfinity;
        text = item.slice(2);
      }

      if (item.indexOf('&discount;') === 0) {
        icon = IconDiscount;
        text = item.replace('&discount;', '');
      }

      if (item.indexOf('&ruble;') === 0) {
        icon = IconRuble;
        text = item.replace('&ruble;', '');
      }

      return {
        icon,
        text,
      };
    });
  } catch (error) {
    console.error(new Error(error));

    return {};
  }
};
